import { configuration, updateConfiguration } from './configuration'
import { icons } from './icons'
import {
  clearTablesListener,
  isDevelopment,
  menuItemsListener,
  reactElementSelectorListener,
  searchListener,
  serverCheckerListener,
  toggleDevToolsListener,
  toggleElementSelectorListener,
  toggleSizeListener,
} from './initHelpers'

const init = (_initConfiguration: typeof configuration) => {
  const existingDevTools = document.querySelector('#fu-tools')

  if (existingDevTools) {
    return
  }

  updateConfiguration(_initConfiguration)

  document.body.insertAdjacentHTML(
    'beforeend',
    `<div id="fu-tools" class="select-none fixed bottom-2 left-1/2 transform -translate-x-1/2 pointer-events-none z-999999">
      <div class="bg-[#1e2939] rounded-lg inline-flex items-center pointer-events-auto cursor-pointer group hover:bg-[#101828] transition-all duration-200 relative">
        <div class="bg-[#364153] rounded-full w-6 h-6 flex items-center justify-center ml-2 my-2">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-[#99a1af] group-hover:text-[#00d3f3] duration-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">${icons.devtools}</svg>
        </div>
        <span class="text-white text-[12px]/[18px] font-mono px-2">DEVTOOLS</span>
        <div id="fu-tools-element-selector" data-fu-tools-element-selector="inactive" class="h-10 flex items-center ${isDevelopment() ? '' : 'fu-tools-element-selector-not-available'} [&.fu-tools-element-selector-not-available]:pr-1 [&:not(.fu-tools-element-selector-not-available)]:pr-2.5 py-2 data-[fu-tools-element-selector=active]:text-[#00d3f3] data-[fu-tools-element-selector=inactive]:text-[#99a1af] flex group-hover:text-[#00d3f3]! group/rotate">
          <svg class="[.fu-tools-element-selector-not-available_&]:hidden duration-200 group-hover/rotate:rotate-360 transition-all" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">${icons.selectors}</svg>
        </div>
        <div data-fu-tools-badge-raw="" data-fu-tools-badge="" class="data-[fu-tools-badge]:[&:not([data-fu-tools-badge=''])]:after:bg-[#ff6467] after:transition after:duration-200 after:h-5 after:w-5 after:-top-2 after:-right-2 after:text-center after:rounded-full after:content-[attr(data-fu-tools-badge)] after:text-white after:text-[14px]/[20px] after:absolute"></div>
      </div>
    </div>

    <div id="fu-tools-content" data-fu-tools-content="closed" data-fu-tools-size="default" class="flex-col hidden data-[fu-tools-content=open]:flex overflow-auto font-mono w-full h-full data-[fu-tools-size=small]:max-h-[450px] data-[fu-tools-size=small]:max-w-7/10 data-[fu-tools-size=default]:max-h-[550px] data-[fu-tools-size=default]:max-w-9/10 data-[fu-tools-size=large]:max-h-9/10 data-[fu-tools-size=large]:max-w-[calc(100%-50px)] transition-all duration-200 bg-[#1e2939] rounded-lg shadow-lg fixed bottom-15 left-1/2 transform -translate-x-1/2 z-999999"> 
      <div class="bg-[#364153] p-4 gap-2 flex-row flex items-center justify-between">
        <div>
          <span class="text-white font-semibold">FaceUp</span>
          <a target="_blank" ${isDevelopment() ? '' : `href="https://faceup.sentry.io/issues/?project=5425818&query=url%3A${encodeURIComponent(configuration.apiUrl)}&sort=date&statsPeriod=30d"`} class="[&:not([href])]:cursor-auto hidden min-[350px]:inline bg-[#4a5565]! text-[#d1d5dc]! [&[href]]:hover:text-white! text-xs rounded-full px-2 py-1 transition duration-200">DevTools</a>
        </div>
        <div class="flex flex-row gap-2 items-center">
          <div title="Open GraphQL Playground" id="fu-tools-server-checker" class="hidden md:flex items-center justify-between bg-[#4a5565] text-[#d1d5dc] text-xs gap-2 rounded-full p-4 py-2 group hover:text-white cursor-pointer transition duration-200">
            <div data-fu-tools-server-status class="w-2 h-2 data-[fu-tools-server-status]:bg-[#99a1af] data-[fu-tools-server-status=online]:bg-[#05df72] data-[fu-tools-server-status=offline]:bg-[#ff6467] rounded-full"></div>
            <span>${configuration.apiUrl}</span>
            <a title="Open in new tab" href="${configuration.apiUrl}" target="_blank" class="text-[#d1d5dc]! hover:text-white! duration-200 transition-all flex">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">${icons.server}</svg>
            </a>
          </div>
          <div id="fu-tools-size" class="text-[#99a1af] flex hover:text-[#00d3f3] cursor-pointer transition duration-200" title="Toggle maximization">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">${icons.toggleSize}</svg>
          </div>
        </div>
      </div>

      <div data-fu-tools-menu-active="${configuration.plugins[0]?.id}" class="flex bg-[#1e2939] flex-1 overflow-hidden relative">
        <div class="text-center py-2 border-[#4a5565] border-r">
          <div class="p-1 gap-1 flex flex-col text-[#99a1af]">
            ${configuration.plugins
              .map(
                ({ icon, name, id }) => `
                <div data-fu-tools-menu="${id}" title="${name}" class="flex items-center justify-center transition-all hover:bg-[#364153] hover:text-[#00d3f3] rounded-md cursor-pointer p-2 relative">
                  <div data-fu-tools-menu-badge-raw="" data-fu-tools-menu-badge="" class="data-[fu-tools-menu-badge]:[&:not([data-fu-tools-menu-badge=''])]:after:bg-[#ff6467] after:transition after:duration-200 after:h-4 after:w-4 after:-top-0.5 after:-right-0.5 after:text-center after:rounded-full after:content-[attr(data-fu-tools-menu-badge)] after:text-white after:text-[10px]/[16px] after:absolute"></div>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">${icon}</svg>
                </div>`
              )
              .join('')}
          </div>
        </div>

        <div class="flex-1 overflow-auto">
        ${configuration.plugins
          .map(
            ({ id, name, layout, extra }) => `
          <div class="h-full" data-fu-tools-content="${id}">
            ${layout.type === 'full' ? layout.render() : ''}
            ${
              layout.type === 'table'
                ? `
            <div class="pt-4 px-4 flex items-center justify-between mb-2 text-white gap-2">
              <span class="font-semibold block">${name}</span>
              <div class="gap-2 flex flex-row">
                ${extra}
                <div class="cursor-pointer flex" data-fu-tools-clear-table="${id}" title="Clear">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-[#99a1af] hover:text-[#00d3f3] duration-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">${icons.clear}</svg>
                </div>
              </div>
            </div>
            <div class="pb-4 px-4 min-w-full divide-y divide-[#364153]">
              ${layout.searchColumnsIndex ? `<input type="text" data-fu-tools-search-input="${id}" data-fu-tools-search-input-columns="${layout.searchColumnsIndex}" class="w-full bg-[#364153] p-2 my-2 text-[12px]/[14px] text-[#d1d5dc] placeholder-[#99a1af] rounded-md border-0 focus:ring-2 focus:ring-[#00d3f3] focus:outline-none" placeholder="Search..." />` : ''}
              <div class="flex sticky top-0 bg-[#1e2939]">
              ${(layout.type === 'table' ? layout.columns : [])
                .map(item =>
                  Array.isArray(item)
                    ? item
                        .map(
                          i =>
                            `<div class="px-4 py-2 text-left text-xs font-medium text-[#d1d5dc] uppercase w-full">${i}</div>`
                        )
                        .join('')
                    : `<div class="px-4 py-2 text-left text-xs font-medium text-[#d1d5dc] uppercase w-full ${item.class}">${item.name}</div>`
                )
                .join('')}
              </div>
              <div data-fu-tools-table-content="${id}" class="divide-y divide-[#364153]"></div>
            </div>`
                : ''
            }
          </div>`
          )
          .join('')}
        </div>
      </div>
    </div>

    <dialog id="fu-tools-dialog" class="top-0 left-0 m-0 p-0 font-mono z-9999999">
      <div class="fixed inset-0 bg-[#101828] opacity-50"></div>
      <div class="fixed inset-0 flex items-center justify-center">
        <div class="text-white bg-[#1e2939] rounded-lg p-4 max-w-9/10 w-full max-h-9/10 flex flex-col">
          <div class="outline-0 flex-1 overflow-auto mt-1 break-words whitespace-break-spaces" id="fu-tools-dialog-content"></div>
          <form method="dialog" class="flex justify-end mt-4">
            <button class="bg-[#364153] hover:bg-[#4a5565] transition duration-200 font-bold py-2 px-4 rounded border-0 cursor-pointer text-white!" type="submit">Close</button>
          </form>
        </div>
      </div>
    </dialog>

    <style>
      ${configuration.plugins
        .map(
          ({ id }) => `
        [data-fu-tools-menu-active="${id}"] [data-fu-tools-menu="${id}"] {
          background-color: #364153;
          color: #00d3f3;
        }
        [data-fu-tools-menu-active]:not([data-fu-tools-menu-active="${id}"]) [data-fu-tools-content="${id}"] {
          display: none;
        }
      `
        )
        .join('')}

      [data-fu-tools-element-selector="active"] #app * {
        outline-offset: -2px !important;
        transition: 0.2s;
        outline-color: rgba(0, 255, 255, 0.5) !important;
      }
      [data-fu-tools-element-selector="active"] #app *:hover {
        cursor: pointer !important;
        outline-width: 2px !important;
        outline-style: solid !important;
        position: relative !important;
      }
      [data-fu-tools-element-selector="active"] #app *:not([data-fu-tools-element-name=""]):not(:has(*:hover)):hover::before {
        content: attr(data-fu-tools-element-name) !important;
        position: absolute !important;
        text-transform: none !important;
        left: 0 !important;
        top: 24px !important;
        background-color: rgba(0, 255, 255, 0.5) !important;
        color: black !important;
        padding: 2px 4px !important;
        font-size: 12px !important;
        font-weight: bold !important;
        border-radius: 4px !important;
        z-index: 9999 !important;
        white-space: nowrap !important;
        pointer-events: none !important;
        transform: translateY(-100%) !important;
        transform-origin: bottom left !important;
        transition: transform 0.2s ease-in-out !important;
        transition-delay: 0s !important;
      }
    </style>`
  )

  clearTablesListener()
  menuItemsListener()
  toggleSizeListener()
  toggleDevToolsListener()
  toggleElementSelectorListener()
  searchListener()
  serverCheckerListener()
  reactElementSelectorListener()

  Object.values(configuration.plugins).forEach(({ init }) => init?.())

  document.addEventListener('fu-tools-event', e => {
    const { pluginId, params } = (e as CustomEvent).detail
    if (!params || !pluginId) {
      return
    }

    const plugin = configuration.plugins.find(plugin => plugin.id === pluginId)
    if (!plugin) {
      console.warn(`[FuTools]: Unknown event type for '${pluginId}'`)
      return
    }

    plugin.render(...params)
  })
}

export { init }
